import React, { useCallback, useMemo, useRef } from "react";

import {
  BULLETIN_SECTION_IDS,
  BulletinSectionId,
} from "@/components/alerts/add-v2/sections/sectionIds";

/**
 * A section's DOM id. Still needed even though the nodes themselves are held by ref: `aria-controls`
 * on the section header takes an id string, not a node.
 */
export const sectionAnchorId = (id: BulletinSectionId): string =>
  `bulletin-section-${id}`;

interface SectionAnchors {
  /** One ref callback per section, for the section's outer element. */
  sectionRefs: Record<BulletinSectionId, React.RefCallback<HTMLDivElement>>;
  scrollToSection: (id: BulletinSectionId) => void;
}

/**
 * Holds the section elements React already renders, so the rail, the xs stepper and
 * jump-to-first-issue can scroll to one without looking it up in the document.
 */
export const useSectionAnchors = (): SectionAnchors => {
  const nodes = useRef(new Map<BulletinSectionId, HTMLDivElement>());

  // Built once. A fresh ref callback per render would make React detach and re-attach the node
  // every time, and would defeat the per-section `React.memo` phase 2 adds.
  const sectionRefs = useMemo(
    () =>
      BULLETIN_SECTION_IDS.reduce(
        (refs, id) => {
          refs[id] = (node) => {
            if (node) nodes.current.set(id, node);
            else nodes.current.delete(id);
          };
          return refs;
        },
        {} as Record<BulletinSectionId, React.RefCallback<HTMLDivElement>>,
      ),
    [],
  );

  const scrollToSection = useCallback((id: BulletinSectionId) => {
    nodes.current
      .get(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return { sectionRefs, scrollToSection };
};
